import { useModelLabel } from "@judo/i18n";
import type { Input, TextArea } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import { TextField } from "@mui/material";
import { buildIconAdornment } from "../utils/icon-adornment";

export interface TextAreaComponentProps {
	element: TextArea | Input;
	readOnly?: boolean;
	value?: string;
	onChange?: (value: string) => void;
	error?: string;
	disabled?: boolean;
	judoClassName?: string;
}

/**
 * TextArea component.
 */
export function TextAreaComponent({
	element,
	readOnly,
	value = "",
	onChange,
	error,
	disabled,
	judoClassName,
}: TextAreaComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const textAreaElement = element as TextArea;
	const startAdornment = buildIconAdornment(element.icon);

	return (
		<TextField
			multiline
			minRows={textAreaElement.lines}
			maxRows={20}
			label={label}
			value={value}
			onChange={(e) => onChange?.(e.target.value)}
			error={!!error}
			helperText={
				error ??
				(textAreaElement.countCharacters
					? `${value.length}${textAreaElement.maxLength ? `/${textAreaElement.maxLength}` : ""}`
					: undefined)
			}
			disabled={disabled ?? element.disabled}
			required={element.required}
			fullWidth
			className={judoClassName}
			data-testid={testId}
			slotProps={{
				input: {
					readOnly: readOnly ?? element.isReadOnly,
					startAdornment,
				},
				htmlInput: {
					maxLength: textAreaElement.maxLength,
				},
				inputLabel: {
					shrink: true,
				},
			}}
		/>
	);
}
